import { useCodeiumFlag } from "commands/definitions/settings/useCodeiumCommand";
import { useGlobalModal } from "components/GlobalModal/useGlobalModal";
import { useProjectSettingsContext } from "components/Settings/SettingsProvider";
import { useEditor } from "features/editor/Feature";
import type { IconType } from "prism-react";
import { Badge, Box, Button, Divider, Icon, Stack, Text } from "prism-react";
import { useEffect } from "react";

import { useActiveWorkspaceSubscription } from "hooks/useActiveWorkspaceSubscription";
import { useAnalytics } from "hooks/useAnalytics";
import { useGlobalPersistedState } from "hooks/usePersistedState";
import { paths } from "utils/router";

import { SANDBOX_FREE_USAGE_WEAK_LIMITATION } from "./CodeiumIntegration";

const CodeiumModal: React.FC<{ close: () => void }> = ({ close }) => {
  const track = useAnalytics();
  const [codeCompletion, setCodeCompletion] = useCodeiumFlag();
  const { workspace } = useEditor();
  const { isFree } = useActiveWorkspaceSubscription();

  useEffect(() => {
    track("codeium_opened_modal");
  }, []);

  const { setActiveTab, setVisibility } = useProjectSettingsContext();
  const [, setHaveSeenCodeiumModal] = useGlobalPersistedState(
    "CODEIUM_MODAL_DISCARDED",
    false,
  );

  const dismiss = () => {
    close();
    setHaveSeenCodeiumModal(true);
  };

  return (
    <Stack>
      <Stack css={{ justifyContent: "space-between" }} horizontal>
        <Badge css={{ display: "inline-flex" }}>New</Badge>

        <Button icon="close" onClick={dismiss} />
      </Stack>

      <Text size="lg">AI code auto-complete.</Text>

      <Text as="h1" color="neutral-fg-high" size="sm">
        {isFree
          ? "Enable powerful AI autocompletion with Codeium, included in your Free workspace."
          : "Enable powerful AI autocompletion with Codeium."}
      </Text>

      {isFree && (
        <>
          <Box
            css={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gridColumnGap: "$6",
              marginTop: "$6",
              paddingBottom: "$6",
            }}
          >
            <Section iconType="sandbox">
              Up to {SANDBOX_FREE_USAGE_WEAK_LIMITATION} free suggestions per
              month in any project.
            </Section>

            <Section iconType="sparkleFill">
              <a
                href={paths.dashboard.upgrade(workspace?.id ?? "")}
                onClick={() => track("codeium_upgrade_to_pro")}
                rel="noreferrer"
                target="_blank"
              >
                Upgrade to Pro
              </a>{" "}
              for unlimited usage everywhere.
            </Section>
          </Box>

          <Divider />
        </>
      )}

      <Stack
        css={{
          marginTop: "$6",
          paddingBottom: "$2",
          justifyContent: "flex-end",
        }}
        horizontal
      >
        <Button
          as="a"
          href="https://codesandbox.io/docs/learn/ai/codeium"
          icon="externalLink"
          iconPosition="right"
          target="_blank"
        >
          Learn more
        </Button>

        {codeCompletion ? (
          <Button
            key="settings"
            onClick={() => {
              dismiss();
              setVisibility(true);
              setActiveTab("preferences");
            }}
            variant="primary"
          >
            Manage settings
          </Button>
        ) : (
          <Button
            key="settings"
            onClick={() => {
              dismiss();
              setCodeCompletion(true);
            }}
            variant="primary"
          >
            Enable Codeium
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

export const useCodeiumModal = () =>
  useGlobalModal(CodeiumModal, { maxWidth: 720 });

const Section: React.FC<{
  children: React.ReactNode;
  iconType: IconType;
}> = ({ children, iconType }) => (
  <Stack gap={3}>
    <Box>
      <Icon size={6} type={iconType} />
    </Box>
    <Stack css={{ justifyContent: "space-between", height: "100%" }} gap={2}>
      <Stack>
        <Text color="neutral-fg-base" css={{ lineHeight: 1.4 }}>
          {children}
        </Text>
      </Stack>
    </Stack>
  </Stack>
);
